import { JobCatalogEnumsJobCatalogTreeNodeTypesEnum } from 'generated/resources/graphql'

import {
  Hierarchy,
  PreviewRow,
  TreeNode,
  Workplace,
} from 'modules/cost_centers/components/CostCentersStructure/types'

interface UsePreviewDataParams {
  treeArray: TreeNode[]
  jobStructure?: JobCatalogEnumsJobCatalogTreeNodeTypesEnum
  expandWorkplace: boolean
  workplaces: Workplace[]
}

interface JobCatalogLeafNode {
  familyName?: string
  functionName?: string
  roleName?: string
}

/**
 * Extracts leaf nodes from the JC tree at the specified hierarchy level.
 * For each leaf, it also collects ancestor names (family, function, role)
 * based on how deep the selected level is in the hierarchy.
 */
const extractJobCatalogLeaves = (
  treeArray: TreeNode[],
  jobStructure: JobCatalogEnumsJobCatalogTreeNodeTypesEnum
): JobCatalogLeafNode[] => {
  const result: JobCatalogLeafNode[] = []
  const targetIndex = Hierarchy.indexOf(jobStructure)

  const traverse = (node: TreeNode, depth: number, ancestors: string[]) => {
    const currentHierarchyIndex = Hierarchy.indexOf(node.type)

    if (currentHierarchyIndex === targetIndex) {
      const leaf: JobCatalogLeafNode = {}

      // Assign ancestor names based on depth
      // ancestors[0] = family, ancestors[1] = function, etc.
      if (ancestors.length > 0) leaf.familyName = ancestors[0]
      if (ancestors.length > 1) leaf.functionName = ancestors[1]

      // The current node is also a dimension level
      if (targetIndex === 0) leaf.familyName = node.name
      else if (targetIndex === 1) leaf.functionName = node.name
      else if (targetIndex === 2) leaf.roleName = node.name

      result.push(leaf)
      return
    }

    // Continue traversing children, collecting this node as an ancestor
    for (const child of node.children || []) {
      traverse(child, depth + 1, [...ancestors, node.name])
    }
  }

  // Start from root's children (families)
  const root = treeArray[0]
  if (!root) return result

  for (const child of root.children || []) {
    traverse(child, 0, [])
  }

  return result
}

/**
 * Builds flat preview rows based on the configured structures.
 * This is a pure computation (no memoization) — the data is small and
 * synchronous, and the caller (Table.tsx) handles reactivity via key-based remounting.
 *
 * - JC only: One row per leaf JC node at the selected hierarchy level
 * - Workplace only: One row per workplace
 * - JC + Workplace: Cartesian product (every JC leaf x every workplace)
 */
export const buildPreviewRows = ({
  treeArray,
  jobStructure,
  expandWorkplace,
  workplaces,
}: UsePreviewDataParams): PreviewRow[] => {
  const hasJobCatalog = !!jobStructure
  const hasWorkplace = expandWorkplace && workplaces.length > 0

  if (!hasJobCatalog && !hasWorkplace) return []

  // JC only
  if (hasJobCatalog && !hasWorkplace) {
    const leaves = extractJobCatalogLeaves(treeArray, jobStructure)
    return leaves.map((leaf, index) => {
      const nameParts = [leaf.familyName, leaf.functionName, leaf.roleName].filter(Boolean)
      return {
        id: `jc-${index}`,
        name: nameParts.join(' - '),
        familyName: leaf.familyName,
        functionName: leaf.functionName,
        roleName: leaf.roleName,
      }
    })
  }

  // Workplace only
  if (!hasJobCatalog && hasWorkplace) {
    return workplaces.map((wp) => ({
      id: `wp-${wp.id}`,
      name: wp.name,
      workplaceName: wp.name,
    }))
  }

  // Cartesian product: JC x Workplace
  if (!jobStructure) return []
  const leaves = extractJobCatalogLeaves(treeArray, jobStructure)
  const rows: PreviewRow[] = []

  for (const [leafIndex, leaf] of leaves.entries()) {
    for (const wp of workplaces) {
      const jcParts = [leaf.familyName, leaf.functionName, leaf.roleName].filter(Boolean)
      const nameParts = [...jcParts, wp.name]

      rows.push({
        id: `jc-${leafIndex}-wp-${wp.id}`,
        name: nameParts.join(' - '),
        familyName: leaf.familyName,
        functionName: leaf.functionName,
        roleName: leaf.roleName,
        workplaceName: wp.name,
      })
    }
  }

  return rows
}
